import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { traceSpan } from "@/lib/sentry-spans";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { TMTimesheetRow } from "./use-tm-invoice-data";
import { addAdHocInterimToPlan } from "./use-invoice-plan";
import { describeSendFailure } from "@/lib/invoice-send";

export type InvoiceType = "startup" | "interim" | "final" | "upsell";

export type InvoiceDeliveryType = "Auto" | "EHF" | "PdfByEmail" | "PdfPrintForDownload";

export interface CreateInvoiceLine {
  description: string;
  quantity?: number;
  unit_price?: number;
  product_code?: string;
  line_type?: "Normal" | "Text";
}

export interface CreateInvoiceInput {
  project_id: string;
  invoice_type: InvoiceType | "credit_note";
  /**
   * Stable idempotency key, generated ONCE when the invoice dialog opens and
   * reused on retry (TOPP-6 / BUG-H2). A new dialog session = a new key, which
   * intentionally distinguishes a deliberate re-invoice from an accidental
   * double-click. Sent as the x-idempotency-key header so server-side dedup
   * can match repeated requests.
   */
  idempotency_key: string;
  lines: CreateInvoiceLine[];
  description?: string;
  customer_reference?: string;
  purchase_order_reference?: string;
  contract_no?: string;
  send?: boolean;
  delivery_type?: InvoiceDeliveryType;
  delivery_email?: string;
  related_invoice_id?: string;
  /** VIB-3030: kunden fakturaen skal til. Mangler den, bruker create-invoice prosjektets kunde. */
  customer_id?: string;
  /** BUG-M2: 0-basert milepæl-posisjon. Settes kun når fakturaen er en milepæl. */
  milestone_index?: number | null;
  /** VIB-1181: betalingsfrist (dager) for denne fakturaen. Overstyrer prosjektets default. */
  payment_due_days?: number;
  /**
   * VIB-1186: T&M-kontekst. Når satt markeres disse timeføringene/bilagene som
   * fakturert (`invoiced_invoice_id`) etter at fakturaen er opprettet, så de
   * ikke tas med på neste T&M-faktura. `timesheet` blir PDF-vedlegg på SalesOrder.
   */
  tm_time_entry_ids?: string[];
  tm_vouchers?: { id: string; markupPct: number }[];
  timesheet?: TMTimesheetRow[];
  /**
   * VIB-994: satt når fakturaen kommer fra et T&M-mersalg. Kobles til
   * `invoices.upsell_id` etter opprettelse (klient-side, samme mønster som
   * `tm_time_entry_ids`/`tm_vouchers` under) — IKKE `added_to_invoice_id`
   * (én-til-én-modellen gjelder ikke T&M: ett mersalg kan ha flere fakturaer
   * ved løpende regning).
   */
  tm_upsell_id?: string;
  /**
   * Mersalg som tas med som egne linjer på fakturaen (typisk sluttfaktura).
   * Markeres `added_to_invoice_id` = den nye fakturaen etter opprettelse, samme
   * mønster som T&M-timer/bilag, så de ikke lenger vises som «ufakturert mersalg».
   */
  upsell_ids?: string[];
  /**
   * VIB-2836: ad hoc-mellomfaktura. Etter opprettelse legges fakturaen inn i
   * `projects.invoice_terms` som et fakturert ledd på `milestone_index`.
   * Kun klient-side — sendes ikke til create-invoice.
   */
  insert_into_plan?: boolean;
}

export interface CreateInvoiceResult {
  success: boolean;
  invoice_id: string;
  po_sales_order_id: string;
  sales_order_no: number | null;
  poweroffice_invoice_id: number | null;
  amount: number;
  amount_excl_vat: number;
  status: string;
  /** Adressen som faktisk ble brukt (body > prosjekt > kunde). */
  delivery_email?: string | null;
  /** Om den som fakturerte ble funnet som PO-ansatt («Vår ref.»). */
  sales_person_matched?: boolean;
  send_failed?: boolean;
  send_error?: string;
  /** Stabil kode for send-feilen, oversettes av `describeSendFailure`. */
  send_error_code?: string;
  /** PO sin `detail`-tekst for send-feilen. */
  send_error_detail?: string;
  /** VIB-2836: fakturaen ble laget, men kunne ikke legges inn i planen. */
  plan_update_error?: string;
}

/**
 * Opprett (og evt. send) faktura via PO SalesOrder-flyten.
 * idempotency_key sendes som x-idempotency-key så samme call to ganger
 * ikke skaper to fakturaer.
 */
export function useCreateInvoice() {
  const qc = useQueryClient();
  const { activeUser } = useImpersonation();
  return useMutation({
    mutationFn: async (input: CreateInvoiceInput): Promise<CreateInvoiceResult> =>
      // VIB-642: instrumenter faktura-opprettelse mot create-invoice
      // edge function. http-op fanger latency + feilrate mot PowerOffice.
      traceSpan("invoice.create", "http", async () => {
        const idempotencyKey = input.idempotency_key;
        const { insert_into_plan, ...requestBody } = input;
        const res = await fetch(`${SUPABASE_URL}/functions/v1/create-invoice`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            apikey: SUPABASE_ANON_KEY,
            "x-idempotency-key": idempotencyKey,
          },
          // VIB-1184: aktør sendes i body (edge får ikke bruker-JWT).
          body: JSON.stringify({ ...requestBody, actor_user_id: activeUser?.id }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error ?? "Kunne ikke opprette faktura");

        // VIB-1186: marker T&M-timer + bilag som fakturert (kun når kalt fra
        // T&M-flyten). Gjøres etter at SalesOrder er opprettet — feiler dette
        // er fakturaen likevel laget, men items kan dukke opp på nytt
        // (akseptabelt; bedre enn å miste dem).
        const invoiceId = (data as CreateInvoiceResult).invoice_id;
        if (invoiceId) {
          if (input.tm_time_entry_ids?.length) {
            const { error } = await supabase
              .from("time_entries")
              .update({ invoiced_invoice_id: invoiceId })
              .in("id", input.tm_time_entry_ids);
            if (error) throw new Error(`Faktura opprettet, men kunne ikke markere timer: ${error.message}`);
          }
          for (const v of input.tm_vouchers ?? []) {
            const { error } = await supabase
              .from("vouchers")
              .update({ invoiced_invoice_id: invoiceId, invoice_markup_pct: v.markupPct })
              .eq("id", v.id);
            if (error) throw new Error(`Faktura opprettet, men kunne ikke markere bilag: ${error.message}`);
          }
          // Mersalg tatt med som linjer: knytt dem til fakturaen så de ikke
          // lenger teller som «ufakturert mersalg» (jf. useLinkUpsellToInvoice).
          if (input.upsell_ids?.length) {
            const { error } = await supabase
              .from("upsells")
              .update({ added_to_invoice_id: invoiceId })
              .in("id", input.upsell_ids);
            if (error) throw new Error(`Faktura opprettet, men kunne ikke knytte mersalg: ${error.message}`);
          }
          // VIB-994: T&M-mersalg — knytt fakturaen til mersalget via
          // invoices.upsell_id (IKKE added_to_invoice_id: ett T&M-mersalg kan
          // ha flere fakturaer over tid, løpende regning).
          if (input.tm_upsell_id) {
            const { error } = await supabase
              .from("invoices")
              .update({ upsell_id: input.tm_upsell_id })
              .eq("id", invoiceId);
            if (error) throw new Error(`Faktura opprettet, men kunne ikke knytte T&M-mersalg: ${error.message}`);
          }
          // Kreditnota: frigjør postene som hang på den krediterte fakturaen
          // (motsatt av forward-markeringen over) så mersalg/T&M kan faktureres
          // på nytt. Gjelder kun kreditnotaer laget i Revehiet; kreditering
          // gjort direkte i PO og synket inn treffer ikke denne flyten.
          if (input.invoice_type === "credit_note" && input.related_invoice_id) {
            const relId = input.related_invoice_id;
            const resets = await Promise.all([
              supabase.from("upsells").update({ added_to_invoice_id: null }).eq("added_to_invoice_id", relId),
              supabase.from("time_entries").update({ invoiced_invoice_id: null }).eq("invoiced_invoice_id", relId),
              supabase.from("vouchers").update({ invoiced_invoice_id: null }).eq("invoiced_invoice_id", relId),
            ]);
            const resetErr = resets.find((r) => r.error)?.error;
            if (resetErr) {
              throw new Error(`Kreditnota opprettet, men kunne ikke frigjøre fakturerte poster: ${resetErr.message}`);
            }
          }
          // VIB-2836: ad hoc-mellomfaktura legges inn i planen. Fakturaen er
          // allerede laget i PO, så en feil her skal ikke se ut som at
          // fakturaen feilet (da ville PL prøvd igjen og fakturert dobbelt).
          // Gjenstående regnes fra fakturaene uansett; planen kan rettes i Detaljer.
          if (insert_into_plan && input.milestone_index != null) {
            try {
              await addAdHocInterimToPlan({
                projectId: input.project_id,
                milestoneIndex: input.milestone_index,
                amountExclVat: Number((data as CreateInvoiceResult).amount_excl_vat ?? 0),
              });
            } catch (err) {
              return { ...(data as CreateInvoiceResult), plan_update_error: (err as Error).message };
            }
          }
        }
        return data;
      }),
    onSuccess: async (data, vars) => {
      await invalidateFor(qc, "invoices", { projectId: vars.project_id });
      if (vars.tm_time_entry_ids?.length) {
        await invalidateFor(qc, "time_entries", { projectId: vars.project_id });
      }
      if (vars.tm_vouchers?.length) {
        await invalidateFor(qc, "vouchers", { projectId: vars.project_id });
      }
      if (vars.upsell_ids?.length) {
        await invalidateFor(qc, "upsells", { projectId: vars.project_id });
      }
      if (vars.insert_into_plan) {
        await invalidateFor(qc, "projects", { projectId: vars.project_id });
      }
      // VIB-994: "fakturert hittil"-badgen i MersalgTab leser invoices via
      // useProjectDetail (project-detail), som allerede invalideres av
      // invalidateFor(qc, "invoices", ...) over — ingen ekstra invalidering
      // nødvendig her, men invalider "upsells" òg for godt mål (samme mønster
      // som upsell_ids-grenen over, i tilfelle en fremtidig visning leser
      // useUpsells i stedet for project-detail).
      if (vars.tm_upsell_id) {
        await invalidateFor(qc, "upsells", { projectId: vars.project_id });
      }
      // Kreditnota frigjorde mersalg + T&M-timer/bilag: invalider alle tre så
      // «ufakturert»-listene oppdateres.
      if (vars.invoice_type === "credit_note" && vars.related_invoice_id) {
        await invalidateFor(qc, "upsells", { projectId: vars.project_id });
        await invalidateFor(qc, "time_entries", { projectId: vars.project_id });
        await invalidateFor(qc, "vouchers", { projectId: vars.project_id });
      }
      // Send-feil er ikke en suksess: dialogen holder seg åpen med forklaring
      // (InvoiceSendFailedAlert), og toasten peker dit i stedet for å si «opprettet».
      if (data.send_failed) {
        const text = describeSendFailure({ code: data.send_error_code, detail: data.send_error_detail });
        toast.warning(text.title, { description: text.action, duration: 10000 });
      } else {
        toast.success(`Faktura ${vars.send ? "opprettet og sendt" : "opprettet som utkast"}`);
      }
      if (data.plan_update_error) {
        toast.warning(
          `Mellomfakturaen ble ikke lagt inn i faktureringsplanen (${data.plan_update_error}). Rett planen under Detaljer.`,
        );
      }
    },
    onError: (err) => {
      toast.error(`Kunne ikke opprette faktura: ${(err as Error).message}`);
    },
  });
}

/**
 * Hent faktura-PDF fra get-invoice-pdf edge-funksjon. Returnerer en blob-URL
 * som kan åpnes i ny tab. Bruker samme mønster som fetchVoucherDocumentBlob.
 */
export async function fetchInvoicePdfBlob(
  invoiceId: string,
): Promise<{ blobUrl: string; contentType: string }> {
  const url = `${SUPABASE_URL}/functions/v1/get-invoice-pdf?invoice_id=${invoiceId}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      apikey: SUPABASE_ANON_KEY,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Kunne ikke hente PDF (${res.status}): ${text}`);
  }
  const contentType = res.headers.get("content-type") ?? "application/pdf";
  const blob = await res.blob();
  return { blobUrl: URL.createObjectURL(blob), contentType };
}

interface UpdateVars {
  invoice_id: string;
  project_id: string;
  patch: { invoice_type?: InvoiceType | null };
}

/**
 * Lar superadmin overstyre invoice_type på enkelt-fakturaer fra Tilbud-tab.
 * Brukes primært for å rydde PO-synkede fakturaer som ikke har type satt
 * (PO returnerer ikke vår "type"-konsept), så Oppstartsfaktura-banneret og
 * lukke-blockeren slutter å vise feil.
 *
 * Invaliderer project-detail så banner/closing-validation re-evaluerer
 * umiddelbart.
 */
export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ invoice_id, patch }: UpdateVars) => {
      const { error } = await supabase
        .from("invoices")
        .update(patch)
        .eq("id", invoice_id);
      if (error) throw new Error(error.message);
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "invoices", { projectId: vars.project_id });
    },
    onError: (err) => {
      toast.error(`Kunne ikke oppdatere faktura: ${(err as Error).message}`);
    },
  });
}
